import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock, MapPin, Music, Play, Ticket } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { BlurFade, Reveal } from "@/components/public/Motion";

/** Poster-frame player for a BKA-supplied MP4. Starts as a still with a play button. */
function StoryPlayer({ item, testId }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);

  const start = () => {
    const v = videoRef.current;
    if (!v) return;
    setPlaying(true);
    v.play().catch(() => {
      setPlaying(false);
      setFailed(true);
    });
  };

  return (
    <figure
      data-testid={testId}
      className="group overflow-hidden rounded-[28px] border border-bka-emerald/15 bg-white shadow-[0_30px_70px_-45px_rgba(13,51,38,0.65)]"
    >
      <div className="relative aspect-video bg-bka-navy">
        <video
          ref={videoRef}
          src={mediaUrl(item.video_url)}
          poster={item.poster_url ? mediaUrl(item.poster_url) : undefined}
          controls={playing}
          playsInline
          preload="metadata"
          onError={() => {
            setPlaying(false);
            setFailed(true);
          }}
          className="h-full w-full object-cover"
          data-testid={`${testId}-video`}
        />
        {!playing && (
          <button
            type="button"
            onClick={start}
            aria-label={`Play video: ${item.name || "BKA story"}`}
            data-testid={`${testId}-play`}
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-t from-bka-navy/70 via-bka-navy/10 to-transparent transition-colors duration-300 hover:from-bka-navy/80"
          >
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-bka-orange text-white shadow-[0_18px_40px_-14px_rgba(143,55,3,0.95)] transition-transform duration-300 group-hover:scale-110">
              <Play className="ml-0.5 h-7 w-7" fill="currentColor" />
            </span>
            {failed && (
              <span
                className="rounded-full bg-bka-navy/80 px-4 py-1.5 text-xs font-semibold text-white"
                data-testid={`${testId}-error`}
              >
                This video could not play in your browser. Tap to try again.
              </span>
            )}
          </button>
        )}
      </div>
      <figcaption className="bka-accent-edge p-6 pl-7">
        <p className="font-display text-lg font-bold leading-snug text-bka-navy">{item.name}</p>
        {item.role && (
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-bka-orangeDark">
            {item.role}
          </p>
        )}
      </figcaption>
    </figure>
  );
}

function YouTubeFrame({ item, testId }) {
  const [active, setActive] = useState(false);
  const id = item.youtube_id;
  const poster =
    (item.thumbnail_url && mediaUrl(item.thumbnail_url)) ||
    `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

  return (
    <figure
      data-testid={testId}
      className="group overflow-hidden rounded-[28px] border border-bka-emerald/15 bg-white shadow-[0_30px_70px_-45px_rgba(13,51,38,0.65)]"
    >
      <div className="relative aspect-video bg-bka-navy">
        {active ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${id}?rel=0`}
            title={item.title || item.name || "Bahamas Kidney Association video"}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
            className="absolute inset-0 h-full w-full"
            data-testid={`${testId}-iframe`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setActive(true)}
            aria-label={`Play video: ${item.title || item.name || "BKA story"}`}
            data-testid={`${testId}-play`}
            className="absolute inset-0 flex items-center justify-center"
          >
            <img src={poster} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <span className="absolute inset-0 bg-gradient-to-t from-bka-navy/70 via-bka-navy/10 to-transparent" />
            <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-bka-orange text-white shadow-[0_18px_40px_-14px_rgba(143,55,3,0.95)] transition-transform duration-300 group-hover:scale-110">
              <Play className="ml-0.5 h-7 w-7" fill="currentColor" />
            </span>
          </button>
        )}
      </div>
      <figcaption className="bka-accent-edge p-6 pl-7">
        <p className="font-display text-lg font-bold leading-snug text-bka-navy">{item.title || item.name}</p>
        {(item.caption || item.role) && (
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-bka-orangeDark">
            {item.caption || item.role}
          </p>
        )}
        {item.description && (
          <p className="mt-3 text-sm font-medium leading-relaxed text-bka-ink/75">{item.description}</p>
        )}
        {item.cta_label && item.cta_href && (
          <a
            href={item.cta_href}
            className="mt-4 inline-flex items-center gap-2 text-sm font-extrabold text-bka-emerald"
            data-testid={`${testId}-cta`}
          >
            {item.cta_label}
          </a>
        )}
      </figcaption>
    </figure>
  );
}

export function TestimonialVideoCard({ item, testId = "testimonial-video" }) {
  return item.youtube_id ? (
    <YouTubeFrame item={item} testId={testId} />
  ) : (
    <StoryPlayer item={item} testId={testId} />
  );
}

export function StoryVideos({ overline, heading, intro, items = [], testId = "home-stories" }) {
  if (!items.length) return null;
  return (
    <section
      id="stories"
      data-testid={testId}
      className="bka-section-sand bka-section-rule border-b border-bka-emerald/10 py-16 lg:py-20"
    >
      <div className="bka-container">
        <BlurFade>
          <p className="bka-overline text-bka-emerald">{overline || "Voices of BKA"}</p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold leading-[1.15] tracking-tight text-bka-navy sm:text-4xl">
            {heading || "Hear It From Our Community"}
          </h2>
          <div className="bka-accent-rule mt-5" />
          {intro && (
            <p className="mt-5 max-w-2xl text-base leading-[1.8] text-bka-ink/80">{intro}</p>
          )}
        </BlurFade>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {items.map((item, i) => (
            <Reveal key={item.id || item.youtube_id || item.video_url || i} delay={i * 0.08}>
              {item.youtube_id ? (
                <YouTubeFrame item={item} testId={`${testId}-item-${i}`} />
              ) : (
                <StoryPlayer item={item} testId={`${testId}-item-${i}`} />
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Announcement band for a BKA event. Every detail comes from the CMS. */
export function EventBand({ event, testId = "home-event" }) {
  if (!event?.title) return null;
  const rows = [
    { icon: CalendarDays, text: event.date },
    { icon: MapPin, text: event.venue },
    { icon: Clock, text: event.times },
    { icon: Music, text: event.entertainment },
    { icon: Ticket, text: event.contribution },
  ].filter((r) => r.text);

  return (
    <section data-testid={testId} className="relative overflow-hidden bg-bka-navy py-16 lg:py-20">
      <div
        className="pointer-events-none absolute -left-20 top-10 h-[380px] w-[380px] rounded-full blur-3xl"
        aria-hidden="true"
        style={{ background: "radial-gradient(circle, rgba(0,107,63,0.5), transparent 70%)" }}
      />
      <div
        className="pointer-events-none absolute -right-24 bottom-0 h-[320px] w-[320px] rounded-full blur-3xl"
        aria-hidden="true"
        style={{ background: "radial-gradient(circle, rgba(200,90,22,0.38), transparent 70%)" }}
      />
      <div className="bka-container relative grid gap-10 lg:grid-cols-12 lg:items-center">
        <Reveal className="lg:col-span-6">
          <p className="bka-overline text-bka-orangeLight">{event.overline || "Upcoming Event"}</p>
          <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-white sm:text-4xl">
            {event.title}
          </h2>
          <div className="bka-accent-rule mt-5" />
          {event.text && (
            <p className="mt-5 max-w-xl text-sm leading-[1.9] text-white/80 sm:text-base">
              {event.text}
            </p>
          )}
          {event.patronage && (
            <p className="mt-5 max-w-xl text-sm italic leading-relaxed text-bka-aqua">
              {event.patronage}
            </p>
          )}
          {event.cta?.href && (
            <Link
              to={event.cta.href}
              data-testid={`${testId}-cta`}
              className="group mt-8 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-bka-orange px-7 text-sm font-bold text-white shadow-[0_14px_34px_-14px_rgba(143,55,3,0.95)] transition-all duration-300 hover:bg-bka-orangeDark"
            >
              {event.cta.label || "Learn More"}
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </Link>
          )}
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-6">
          <ul className="grid gap-3 rounded-[28px] border border-white/15 bg-white/[0.06] p-6 backdrop-blur sm:p-7">
            {rows.map((row, i) => (
              <li key={i} className="flex items-start gap-3 text-white/90">
                <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bka-orange/20 text-bka-orangeLight">
                  <row.icon className="h-4 w-4" />
                </span>
                <span className="text-sm leading-[1.7]">{row.text}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}

/** Image-led strip of authentic BKA photographs with factual captions. */
export function CommunityStrip({ overline, heading, intro, items = [], testId = "home-community" }) {
  if (!items.length) return null;
  return (
    <section data-testid={testId} className="bka-section-orange py-16 lg:py-20">
      <div className="bka-container">
        <BlurFade>
          <p className="bka-overline text-bka-orangeDark">{overline || "BKA In The Community"}</p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-bold leading-[1.15] tracking-tight text-bka-navy sm:text-4xl">
            {heading || "Our Work Across The Islands"}
          </h2>
          <div className="bka-accent-rule mt-5" />
          {intro && (
            <p className="mt-5 max-w-2xl text-base leading-[1.8] text-bka-ink/80">{intro}</p>
          )}
        </BlurFade>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {items.map((item, i) => (
            <Reveal key={item.image_url || i} delay={i * 0.07}>
              <figure
                data-testid={`${testId}-item-${i}`}
                className="group h-full overflow-hidden rounded-[26px] border border-bka-emerald/15 bg-white shadow-[0_26px_60px_-45px_rgba(13,51,38,0.65)] transition-colors duration-300 hover:border-bka-orange/45"
              >
                <div className="h-52 overflow-hidden sm:h-60">
                  <img
                    src={mediaUrl(item.image_url)}
                    alt={item.alt || item.caption || "Bahamas Kidney Association activity"}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                {item.caption && (
                  <figcaption className="p-5 text-sm leading-[1.7] text-bka-ink/80">
                    {item.caption}
                  </figcaption>
                )}
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
